<!-- @format -->
<template>
    <div class="modal" :class="{ 'is-active': isActive }">
        <div class="modal-background" @click="close"></div>
        <div class="modal-card">
            <header class="modal-card-head">
                <p class="modal-card-title">Poll Results - {{ postTitle }}</p>
                <button class="delete" aria-label="close" @click="close"></button>
            </header>
            <section class="modal-card-body">
                <div v-if="isLoading" class="has-text-centered">
                    <span class="icon is-large">
                        <fa-icon icon="spinner" class="fa-spin" />
                    </span>
                    <p>Loading poll results...</p>
                </div>
                
                <div v-else-if="error" class="notification is-danger">
                    {{ error }}
                </div>
                
                <div v-else-if="pollResults">
                    <div v-for="(question, index) in questions" :key="index" class="mb-5">
                        <h4 class="title is-5 mb-3">Question {{ index + 1 }}: {{ question.text }}</h4>

                        <!-- Choice results -->
                        <div v-if="question.choices && question.choices.length > 0">
                            <div v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex" class="mb-3">
                                <div class="is-flex is-justify-content-space-between is-align-items-center">
                                    <span class="mr-3">{{ choice }}</span>
                                    <span class="has-text-weight-semibold">
                                        {{ getChoicePercentage(question.id, choiceIndex) }}%
                                        ({{ getChoiceVotes(question.id, choiceIndex) }})
                                    </span>
                                </div>
                                <progress 
                                    class="progress is-primary mt-1" 
                                    :value="getChoiceVotes(question.id, choiceIndex)" 
                                    :max="getTotalVotes(question.id)"
                                ></progress>
                            </div>

                            <!-- Other option results -->
                            <div v-if="getOtherVotes(question.id) > 0" class="mb-3">
                                <div class="is-flex is-justify-content-space-between is-align-items-center">
                                    <span class="mr-3">Other</span>
                                    <span class="has-text-weight-semibold">
                                        {{ getOtherPercentage(question.id) }}%
                                        ({{ getOtherVotes(question.id) }})
                                    </span>
                                </div>
                                <progress 
                                    class="progress is-primary mt-1" 
                                    :value="getOtherVotes(question.id)" 
                                    :max="getTotalVotes(question.id)"
                                ></progress>
                            
                            </div>
                        </div>

                        <!-- Text responses -->
                        <div v-if="getTextResponses(question.id).length > 0" class="mt-2">
                            <p class="has-text-weight-semibold mb-2">Text responses:</p>
                            <div v-for="(response, responseIndex) in getTextResponses(question.id)" :key="responseIndex" class="box is-small">
                                <div class="is-flex is-justify-content-space-between is-align-items-start">
                                    <div class="mr-3">
                                        <span>"{{ response.text }}"</span>
                                        <div v-if="response.approved" class="mt-1">
                                            <span class="tag is-success is-small">
                                                <fa-icon icon="check" class="mr-1" />
                                                Approved
                                            </span>
                                        </div>
                                    </div>
                                    <div class="buttons">
                                        <button
                                            v-if="!response.approved"
                                            class="button is-small is-success"
                                            :class="{ 'is-loading': response.isApproving }"
                                            :disabled="response.isApproving"
                                            @click="approveResponse(question.id, response)"
                                        >
                                            <span class="icon is-small">
                                                <fa-icon icon="check" />
                                            </span>
                                            <span>Approve</span>
                                        </button>
                                        <button
                                            v-else
                                            class="button is-small is-danger"
                                            :class="{ 'is-loading': response.isApproving }"
                                            :disabled="response.isApproving"
                                            @click="unapproveResponse(question.id, response)"
                                        >
                                            <span class="icon is-small">
                                                <fa-icon icon="times" />
                                            </span>
                                            <span>Unapprove</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="has-text-grey mt-2">
                            <strong>Total votes: {{ getTotalVotes(question.id) }}</strong><br>
                            <strong>Total voters: {{ getTotalVoters(question.id) }}</strong>
                        </div>
                    </div>

                    <div class="has-text-grey mt-2">
                        <strong>Total votes for all questions: {{ getTotalVotesAllQuestions() }}</strong><br>
                        <strong>Total voters for all questions: {{ getTotalVotersAllQuestions() }}</strong>
                    </div>

                    <div v-if="pollEndDate" class="notification is-light mt-4">
                        <strong>Poll ends:</strong> {{ formatDate(pollEndDate) }}
                    </div>
                </div>
            </section>
            <footer class="modal-card-foot">
                <button class="button" @click="close">Close</button>
            </footer>
        </div>
    </div>
</template>

<script>
import { formatDateTime } from '@/helpers/date.js';
import { getPollStatus, approveTextResponse, unapproveTextResponse } from '@/services/poll.js';

export default {
    name: 'PollResultsModal',
    
    props: {
        isActive: {
            type: Boolean,
            default: false
        },
        post: {
            type: Object,
            default: null
        }
    },
    
    data() {
        return {
            isLoading: false,
            error: null,
            pollResults: null,
            questions: [],
            pollEndDate: null,
            pollTotalVoters: 0
        };
    },
    
    computed: {
        postTitle() {
            return this.post ? this.post.title : '';
        }
    },
    
    watch: {
        isActive(newVal) {
            if (newVal && this.post) {
                this.loadPollResults();
            }
        }
    },
    
    methods: {
        formatDate(date) {
            return formatDateTime(date);
        },
        
        async loadPollResults() {
            if (!this.post) return;
            
            this.isLoading = true;
            this.error = null;
            
            try {
                // Get poll questions from post data
                this.questions = [];
                this.pollEndDate = null;
                
                if (this.post.post_polls && this.post.post_polls.length > 0) {
                    this.post.post_polls.forEach(postPoll => {
                        this.questions = this.questions.concat(postPoll.questions || []);
                        if (postPoll.ends_at) {
                            this.pollEndDate = postPoll.ends_at;
                        }
                    });
                }
                
                // Get poll results
                const response = await getPollStatus({
                    data: {
                        post_id: this.post.id
                    }
                });
                
                this.pollResults = response.results;
                this.pollTotalVoters = response.totalVoters;
                console.log('DEBUG: pollResults', this.pollResults);
            } catch (error) {
                console.error('Error loading poll results:', error);
                this.error = 'Failed to load poll results. Please try again.';
            } finally {
                this.isLoading = false;
            }
        },
        
        getChoiceVotes(questionId, choiceIndex) {
            if (!this.pollResults || !this.pollResults[questionId]) return 0;
            return this.pollResults[questionId].choices[choiceIndex] || 0;
        },
        
        getChoicePercentage(questionId, choiceIndex) {
            const votes = this.getChoiceVotes(questionId, choiceIndex);
            const totalVotes = this.getTotalVotes(questionId);
            if (totalVotes === 0) return 0;
            return Math.round((votes / totalVotes) * 100);
        },
        
        getTotalVotes(questionId) {
            if (!this.pollResults || !this.pollResults[questionId]) return 0;
            return this.pollResults[questionId].totalVotes || 0;
        },

        getTotalVoters(questionId) {
            if (!this.pollResults || !this.pollResults[questionId]) return 0;
            return this.pollResults[questionId].totalVoters || 0;
        },

        getTotalVotersAllQuestions() {
            if (!this.pollResults) return 0;
            console.log('DEBUG: getTotalVotersAllQuestions', this.pollResults);
            return this.pollTotalVoters || 0;
        },

        getTotalVotesAllQuestions() {
            if (!this.pollResults) return 0;
            return Object.values(this.pollResults).reduce((acc, question) => acc + question.totalVotes, 0);
        },
        
        getOtherVotes(questionId) {
            if (!this.pollResults || !this.pollResults[questionId]) return 0;
            return this.pollResults[questionId].otherVotes || 0;
        },
        
        getOtherPercentage(questionId) {
            const votes = this.getOtherVotes(questionId);
            const totalVotes = this.getTotalVotes(questionId);
            if (totalVotes === 0) return 0;
            return Math.round((votes / totalVotes) * 100);
        },
        
        getTextResponses(questionId) {
            if (!this.pollResults || !this.pollResults[questionId]) return [];
            return this.pollResults[questionId].otherResponses || [];
        },
        
        async approveResponse(questionId, response) {
            // Add loading state
            this.$set(response, 'isApproving', true);
            
            try {
                await approveTextResponse({
                    post_id: this.post.id,
                    poll_response_id: response.poll_response_id,
                    question_id: questionId,
                    response_text: response.text
                });
                
                // Reload results to get updated approval status
                await this.loadPollResults();
            } catch (error) {
                console.error('Error approving response:', error);
                this.error = 'Failed to approve response. Please try again.';
            } finally {
                this.$set(response, 'isApproving', false);
            }
        },
        
        async unapproveResponse(questionId, response) {
            // Add loading state
            this.$set(response, 'isApproving', true);
            
            try {
                await unapproveTextResponse({
                    post_id: this.post.id,
                    poll_response_id: response.poll_response_id,
                    question_id: questionId
                });
                
                // Reload results to get updated approval status
                await this.loadPollResults();
            } catch (error) {
                console.error('Error unapproving response:', error);
                this.error = 'Failed to unapprove response. Please try again.';
            } finally {
                this.$set(response, 'isApproving', false);
            }
        },
        
        close() {
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
.modal-card {
    max-width: 800px;
    width: 90%;
}

.progress {
    height: 0.75rem;
}
</style> 